import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  ACTION_TABLE_BY_TOOL,
  type ActionProposal,
} from "@/types/mirror";

// Includes the spine-only tables (nutrition_logs / focus_sessions / sleep_logs)
// only to match ACTION_TABLE_BY_TOOL's widened value type. executeProposal never
// produces them — the propose_* spine tools are claimed by useModuleCapture
// before the chip path runs, so they never reach buildRow/executeProposal here.
type ActionTable = "agenda_events" | "notes" | "tasks" | "habits" | "habit_completions" | "nutrition_logs" | "focus_sessions" | "sleep_logs" | "mirror_reminders" | "user_whisper_preferences" | "daily_checkins";

interface ExecuteResult {
  id: string;
  tableName: ActionTable;
}

interface UseMirrorActionsApi {
  executeProposal: (proposal: ActionProposal, actionId: string) => Promise<ExecuteResult>;
  undoAction: (tableName: ActionTable, id: string) => Promise<void>;
}

const PG_UNIQUE_VIOLATION = "23505";

// LOTE 4.1 (05/09/2026) — a cota de ações do cliente FOI REMOVIDA, na raiz.
// `useMirrorActionQuota` lia e escrevia `mirror_action_quota`, tabela que a
// migration 20260806130300 dropou como zumbi ("nada participa de decisão")
// — a auditoria não viu que este hook a lia para o `canWrite` do chip. Desde
// 06/08 a leitura dava 404 em TODA carga do Espelho, `used` ficava 0, e o
// portão de 5 ações/dia do Free nunca disparava: o gate já estava morto, só
// custava um 404 e um "5 de 5 restantes hoje" mentiroso. A cota canônica é a
// do servidor (consume_text_quota_atomic / quota_usage_snapshot): toda ação
// nasce de uma mensagem já contada lá. Um contador no cliente é contornável
// e duplicado — se o Free voltar a ter teto de ações, é no servidor.
export function useMirrorActions(): UseMirrorActionsApi {
  const executeProposal = useCallback(
    async (proposal: ActionProposal, actionId: string): Promise<ExecuteResult> => {
      if (proposal.tool === "propose_file_action") throw new Error("file_review_required");
      if (proposal.tool === 'propose_finance_action' || proposal.tool === 'propose_finance_log') throw new Error('finance_review_required');
      if (proposal.tool === 'propose_nutrition_meal_action') throw new Error('nutrition_meal_review_required');
      if (proposal.tool === 'propose_fitness_curated_plan') throw new Error('fitness_curated_plan_review_required');
      if (proposal.tool === 'propose_language_class_action') throw new Error('language_class_review_required');
      if (proposal.tool === 'prepare_language_tutor_handoff') throw new Error('language_tutor_handoff_required');
      if (proposal.tool === 'propose_brain_note_action') throw new Error('brain_note_review_required');
      if (proposal.tool === 'prepare_brain_handoff') throw new Error('brain_handoff_required');
      if (proposal.tool === 'propose_language_action') throw new Error('language_review_required');
      if (proposal.tool === 'propose_nutrition_goals_action') throw new Error('nutrition_goals_review_required');
      if (proposal.tool === 'propose_nutrition_hydration_action') throw new Error('nutrition_hydration_review_required');
      if (proposal.tool === "propose_goal" || proposal.tool === "propose_goal_action") throw new Error("goal_review_required");
      if (proposal.tool === "create_task" || proposal.tool === "propose_local_task_action") throw new Error("task_review_required");
      if (["create_note", "propose_note_action"].includes(proposal.tool)) throw new Error("note_review_required");
      if (["create_habit", "propose_habit_action", "propose_habit_log"].includes(proposal.tool)) throw new Error("habit_review_required");
      if (["create_agenda_event", "propose_agenda_event", "propose_local_agenda_action"].includes(proposal.tool)) throw new Error("agenda_review_required");
      if (["propose_reminder_action", "propose_reminder_preferences_action"].includes(proposal.tool)) throw new Error("reminder_review_required");
      if (["propose_focus_log", "propose_focus_action", "propose_focus_timer_action"].includes(proposal.tool)) throw new Error("focus_review_required");
      if (["propose_body_log", "propose_body_health_action"].includes(String(proposal.tool))) throw new Error("body_health_review_required");
      if (proposal.tool === "propose_fitness_action") throw new Error("fitness_review_required");
      if (proposal.tool === "propose_fitness_local_draft_discard") throw new Error("fitness_draft_review_required");
      if (proposal.tool === "propose_fitness_runtime_action") throw new Error("fitness_runtime_review_required");
      if (proposal.tool === "propose_fitness_draft_restore") throw new Error("fitness_restore_review_required");
      if (proposal.tool === "propose_fitness_workout_finish") throw new Error("fitness_workout_finish_review_required");
      if (proposal.tool === "propose_fitness_saved_plan_prepare") throw new Error("fitness_saved_plan_prepare_review_required");
      if (proposal.tool === "propose_fitness_rest_skip") throw new Error("fitness_rest_skip_review_required");
      if (proposal.tool === "propose_fitness_current_set_completion") throw new Error("fitness_current_set_completion_review_required");
      if (proposal.tool === "propose_fitness_setup_start") throw new Error("fitness_setup_start_review_required");
      if (proposal.tool === "propose_fitness_current_set_edit") throw new Error("fitness_current_set_review_required");
      if (["propose_sleep_log", "propose_sleep_action"].includes(String(proposal.tool))) throw new Error("sleep_review_required");
      if (String(proposal.tool) === "propose_checkin_action") throw new Error("checkin_review_required");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const tableName = ACTION_TABLE_BY_TOOL[proposal.tool];

      const buildRow = (): Record<string, unknown> => {
        switch (proposal.tool) {
          case "create_agenda_event":
            return {
              user_id: user.id,
              client_action_id: actionId,
              source: "mirror",
              title: proposal.args.title,
              start_at: proposal.args.start_at,
              end_at: proposal.args.end_at ?? null,
              tag: proposal.args.tag,
              notes: proposal.args.notes ?? null,
              reminder_minutes:
                proposal.args.reminder_minutes == null
                  ? null
                  : proposal.args.reminder_minutes,
            };
        }
      };

      const row = buildRow();

      const { data, error } = await (supabase as any)
        .from(tableName)
        .insert(row)
        .select("id")
        .single();

      if (error) {
        if ((error as { code?: string }).code === PG_UNIQUE_VIOLATION) {
          // Idempotent retry — row already exists for this client_action_id.
          const { data: existing, error: fetchErr } = await (supabase as any)
            .from(tableName)
            .select("id")
            .eq("user_id", user.id)
            .eq("client_action_id", actionId)
            .maybeSingle();
          if (fetchErr || !existing?.id) throw fetchErr || error;
          return { id: existing.id as string, tableName };
        }
        throw error;
      }

      const insertedId = (data as { id: string }).id;
      return { id: insertedId, tableName };
    },
    [],
  );

  const undoAction = useCallback(
    async (tableName: ActionTable, id: string) => {
      if (tableName === "sleep_logs") throw new Error("sleep_review_required");
      if (tableName === "daily_checkins") throw new Error("checkin_review_required");
      if (tableName === "focus_sessions") throw new Error("focus_review_required");
      if (tableName === "agenda_events") throw new Error("agenda_review_required");
      if (tableName === "tasks") throw new Error("task_review_required");
      if (tableName === "notes") throw new Error("note_review_required");
      if (tableName === "habits" || tableName === "habit_completions") throw new Error("habit_review_required");
      if (tableName === "mirror_reminders" || tableName === "user_whisper_preferences") throw new Error("reminder_review_required");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await (supabase as any)
        .from(tableName)
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    [],
  );

  return { executeProposal, undoAction };
}
